import { getToken, onMessage, deleteToken, MessagePayload } from "firebase/messaging";
import { doc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { messaging, db } from "./firebase";
import { Globals } from "../globals";
import { gemeindeSubPath } from "./gemeindePaths";
import { migrateStorageKey } from "./storageMigration";

// Public VAPID key for Web Push – safe to commit, not a secret.
// The matching private key must be registered in Firebase Console →
// Project Settings → Cloud Messaging → Web Push certificates.
const VAPID_PUBLIC_KEY =
    "BOlJ8ZHg1MIfRVIaOt4lDFoi_WJYvU5RDB2X0WPKwM8OfywUNTOBukYSIur5u0ea85b7n0JXILOZZji1O2FJzQM";

const FCM_TOKEN_STORAGE_KEY = "serva_fcm_token";
const LEGACY_FCM_TOKEN_STORAGE_KEY = "godilog_fcm_token";

export function isFcmSupported(): boolean {
    return (
        typeof window !== "undefined" &&
        "Notification" in window &&
        "serviceWorker" in navigator &&
        messaging !== null
    );
}

export function getStoredFcmToken(): string | null {
    migrateStorageKey(LEGACY_FCM_TOKEN_STORAGE_KEY, FCM_TOKEN_STORAGE_KEY);
    try {
        return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
    } catch {
        return null;
    }
}

export async function subscribeToPushNotifications(gemeindeId: string, userId: string): Promise<string> {
    if (!messaging) throw new Error("FCM not supported");

    const permission = await Notification.requestPermission();
    if (permission !== "granted") throw new Error("permission-denied");

    const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    // Wait for the service worker to become active before requesting a token
    await navigator.serviceWorker.ready;

    const token = await getToken(messaging, {
        vapidKey: VAPID_PUBLIC_KEY,
        serviceWorkerRegistration: registration,
    });

    // Store the token under the user's Gemeinde so reminders only reach the
    // admins of that tenant.
    await setDoc(
        doc(db, ...gemeindeSubPath(gemeindeId, Globals.FCM_TOKENS_SUBCOLLECTION, token)),
        {
            token,
            userId,
            updatedAt: serverTimestamp(),
            userAgent: navigator.userAgent,
        },
        { merge: true }
    );

    localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
    return token;
}

export async function unsubscribeFromPushNotifications(gemeindeId: string, token: string): Promise<void> {
    if (!messaging) return;

    try {
        await deleteToken(messaging);
    } catch (_) {
        // Token may already be invalid; continue with Firestore cleanup
    }

    await deleteDoc(doc(db, ...gemeindeSubPath(gemeindeId, Globals.FCM_TOKENS_SUBCOLLECTION, token)));
    localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
}

export function onForegroundMessage(callback: (payload: MessagePayload) => void): () => void {
    if (!messaging) return () => {};
    return onMessage(messaging, callback);
}
